// ToastProvider — fila global de toasts (RF-01/02/03). Máx 3 visíveis (FIFO),
// posicionados no topo-centro, entrando/saindo por slide vertical, auto-dismiss
// por tom (pausável no hover e no foco). Features chamam toast.error(...)/danger(...)
// etc, ou o atalho de módulo showError(msg). Context API + reducer local (sem Redux).
//
// Durações, limite da pilha e janela de saída moram em @/config/toast — este
// arquivo não define mais números soltos.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Toast, type ToastAction, type ToastState as ToastLifecycle } from './Toast';
import { computeStackGeometry } from './stack';
import styles from './ToastProvider.module.css';
import {
  TOAST_DURATION,
  TOAST_EXIT_MS,
  TOAST_MAX_VISIBLE,
  TOAST_MOTION_MS,
  TOAST_STACK_PEEK,
  TOAST_STACK_SCALE_STEP,
  type ToastTone,
} from '@/config/toast';

/**
 * Duração default de um toast neutro. Mantido exportado por retrocompatibilidade —
 * o default real agora é por tom, em TOAST_DURATION.
 */
export const DEFAULT_TOAST_MS = TOAST_DURATION.info;

interface ToastInput {
  title: string;
  description?: ReactNode;
  /** ms; 0 = sticky até dismiss manual. Default: TOAST_DURATION[tom]. */
  durationMs?: number;
  /** Botão dentro do toast — hoje o "Desfazer" (RF-06). */
  action?: ToastAction;
  /** Chamado quando o toast sai, por tempo ou por dismiss manual. */
  onDismiss?: () => void;
}

/** Campos alteráveis por update() — inclui o tom, pra fazer loading → success. */
type ToastPatch = Partial<ToastInput> & { tone?: ToastTone };

interface ToastItem extends ToastInput {
  id: string;
  tone: ToastTone;
  /** 'open' = visível/entrando; 'leaving' = tocando a animação de saída. */
  state: ToastLifecycle;
  /**
   * Sobe toda vez que o timer é reagendado. Vira `key` da barra de contagem, que
   * é uma animação CSS: sem remontar o nó, um `update()` com duração nova
   * deixaria a barra correndo no ritmo antigo — mostrando um prazo que não é o
   * que o setTimeout vai cumprir.
   */
  generation: number;
}

interface ToastState {
  items: ToastItem[];
}

type ToastReducerAction =
  | { type: 'add'; item: ToastItem }
  | { type: 'update'; id: string; patch: ToastPatch }
  | { type: 'leave'; id: string }
  | { type: 'remove'; id: string }
  | { type: 'clear' };

function reducer(state: ToastState, action: ToastReducerAction): ToastState {
  switch (action.type) {
    case 'add': {
      // FIFO: ao passar do limite, dropa o mais antigo.
      const next = [...state.items, action.item];
      if (next.length > TOAST_MAX_VISIBLE) next.shift();
      return { items: next };
    }
    case 'update':
      return {
        items: state.items.map((t) =>
          // Reabre um toast que já estava saindo: update() num loading que
          // acabou de expirar não pode ressuscitá-lo pela metade.
          t.id === action.id
            ? { ...t, ...action.patch, state: 'open', generation: t.generation + 1 }
            : t,
        ),
      };
    case 'leave':
      return {
        items: state.items.map((t) => (t.id === action.id ? { ...t, state: 'leaving' } : t)),
      };
    case 'remove':
      return { items: state.items.filter((t) => t.id !== action.id) };
    case 'clear':
      return { items: [] };
    default:
      return state;
  }
}

export interface ToastApi {
  info(input: ToastInput): string;
  success(input: ToastInput): string;
  warning(input: ToastInput): string;
  danger(input: ToastInput): string;
  /** Alias de danger — a interface oficial de erro (RF-01). */
  error(input: ToastInput): string;
  /** Sticky até um update() resolver em success/danger (RF-05). */
  loading(input: ToastInput): string;
  /**
   * Muda um toast já na tela — o par de loading(): mesma "caixa" vira sucesso ou
   * erro sem piscar. No-op se o id já saiu.
   */
  update(id: string, patch: ToastPatch): void;
  dismiss(id: string): void;
  clear(): void;
}

/**
 * Tons em que a descrição continua sendo exibida. Ver o comentário no ponto de
 * uso, na renderização da região.
 */
const DESCRIPTION_TONES = new Set<ToastTone>(['danger', 'warning']);

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast deve ser usado dentro de <ToastProvider>');
  return ctx;
}

// --- Atalho de módulo showError (RF-01) --------------------------------------
// Referência ao provider ativo montado app-wide. Permite disparar um toast de
// erro de qualquer lugar (inclusive fora de componentes) só passando a mensagem.
let activeToastApi: ToastApi | null = null;

/** Entrada aceita por showError: string, {title,description} ou um erro qualquer. */
export type ErrorLike = string | { title: string; description?: ReactNode } | unknown;

/** Normaliza string OU erro num par {title, description} exibível. */
export function normalizeError(input: ErrorLike): ToastInput {
  if (typeof input === 'string') return { title: input };
  if (input instanceof Error) return { title: 'Erro', description: input.message };
  if (input && typeof input === 'object') {
    const o = input as Record<string, unknown>;
    if (typeof o.title === 'string') {
      return { title: o.title, description: o.description as ReactNode };
    }
    if (typeof o.message === 'string' && o.message.trim()) {
      return { title: 'Erro', description: o.message };
    }
    const data = o.data as Record<string, unknown> | undefined;
    if (data && typeof data.message === 'string' && data.message.trim()) {
      return { title: 'Erro', description: data.message };
    }
  }
  return { title: 'Algo deu errado', description: 'Tente novamente.' };
}

/**
 * Dispara um toast de erro central a partir de uma mensagem ou erro cru.
 * Interface oficial de erro (RF-01): "recebe a mensagem e dispara" — a tela não
 * cuida de posição/animação/ciclo de vida. Requer um <ToastProvider> montado.
 */
export function showError(input: ErrorLike): string | undefined {
  const normalized = normalizeError(input);
  if (activeToastApi) return activeToastApi.danger(normalized);
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error('[showError] chamado sem <ToastProvider> montado:', normalized);
  }
  return undefined;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}

export interface ToastProviderProps {
  children: ReactNode;
}

// Estado de um timer de auto-dismiss — guardamos o tempo restante e o instante de
// início pra poder pausar/retomar no hover sem perder o que já correu.
interface TimerEntry {
  timeoutId: ReturnType<typeof setTimeout>;
  remaining: number;
  startedAt: number;
  /**
   * 🔴 Existe porque pause() passou a ter DOIS chamadores para o mesmo toast: a
   * região (hover abre a pilha inteira) e o próprio toast (foco de teclado).
   * Sem a trava, a 2ª chamada recalcula `remaining` a partir de um `startedAt`
   * que a 1ª já consumiu e desconta o tempo decorrido duas vezes — o toast
   * sumiria mais cedo justamente enquanto o usuário o lê.
   */
  paused: boolean;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [state, dispatch] = useReducer(reducer, { items: [] });
  // Alturas medidas por toast. Precisa ser state, não ref: o offset de um toast
  // depende da altura dos que estão à frente, então medir tem que repintar.
  const [heights, setHeights] = useState<Record<string, number>>({});
  // Pilha aberta. Também é o sinal de "timers pausados" — os dois acontecem no
  // mesmo gesto, e a barra de contagem regressiva pausa pelo mesmo data-attr.
  const [expanded, setExpanded] = useState(false);
  const baseId = useId();
  const seqRef = useRef(0);
  // Timers de auto-dismiss (pausáveis) e timers da animação de saída.
  const timersRef = useRef(new Map<string, TimerEntry>());
  const leaveTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // onDismiss por id. Fica fora do reducer porque beginLeave é um useCallback
  // estável e não pode depender de `state` sem recriar todos os timers.
  const dismissCbsRef = useRef(new Map<string, () => void>());
  // Ids ainda no DOM. Não dá pra inferir isso dos timers: um `loading` é sticky
  // (duration 0) e portanto NÃO tem timer, mas existe e pode receber update().
  const liveIdsRef = useRef(new Set<string>());

  const clearDismissTimer = useCallback((id: string) => {
    const entry = timersRef.current.get(id);
    if (entry) {
      clearTimeout(entry.timeoutId);
      timersRef.current.delete(id);
    }
  }, []);

  // Remoção definitiva do DOM (após a animação de saída, ou imediata no clear).
  const remove = useCallback(
    (id: string) => {
      clearDismissTimer(id);
      const leaveTimer = leaveTimersRef.current.get(id);
      if (leaveTimer) {
        clearTimeout(leaveTimer);
        leaveTimersRef.current.delete(id);
      }
      liveIdsRef.current.delete(id);
      dispatch({ type: 'remove', id });
    },
    [clearDismissTimer],
  );

  // Inicia a saída (RF-03): marca leaving, roda a animação e só então remove.
  const beginLeave = useCallback(
    (id: string) => {
      clearDismissTimer(id);
      if (leaveTimersRef.current.has(id)) return; // já saindo
      dispatch({ type: 'leave', id });
      // Avisa o chamador uma única vez, por tempo ou por clique no ×.
      const cb = dismissCbsRef.current.get(id);
      if (cb) {
        dismissCbsRef.current.delete(id);
        cb();
      }
      const delay = prefersReducedMotion() ? 0 : TOAST_EXIT_MS;
      leaveTimersRef.current.set(
        id,
        setTimeout(() => remove(id), delay),
      );
    },
    [clearDismissTimer, remove],
  );

  // (Re)agenda o auto-dismiss de um toast pra daqui a `remaining` ms.
  const scheduleTimer = useCallback(
    (id: string, remaining: number) => {
      timersRef.current.set(id, {
        timeoutId: setTimeout(() => beginLeave(id), remaining),
        remaining,
        startedAt: Date.now(),
        paused: false,
      });
    },
    [beginLeave],
  );

  const pause = useCallback((id: string) => {
    const entry = timersRef.current.get(id);
    if (!entry || entry.paused) return; // sticky, já saindo ou já pausado
    entry.paused = true;
    clearTimeout(entry.timeoutId);
    const elapsed = Date.now() - entry.startedAt;
    entry.remaining = Math.max(0, entry.remaining - elapsed);
  }, []);

  const resume = useCallback(
    (id: string) => {
      const entry = timersRef.current.get(id);
      if (!entry || !entry.paused) return;
      scheduleTimer(id, entry.remaining);
    },
    [scheduleTimer],
  );

  // Hover na região abre a pilha inteira: os toasts de trás ficam legíveis, e
  // manter os timers deles correndo faria sumir na cara do usuário exatamente o
  // que ele acabou de revelar.
  const pauseAll = useCallback(() => {
    timersRef.current.forEach((_, id) => pause(id));
  }, [pause]);

  const resumeAll = useCallback(() => {
    timersRef.current.forEach((_, id) => resume(id));
  }, [resume]);

  const enqueue = useCallback(
    (tone: ToastTone, input: ToastInput): string => {
      seqRef.current += 1;
      const id = `${baseId}-${seqRef.current}`;
      const item: ToastItem = { ...input, id, tone, state: 'open', generation: 0 };
      dispatch({ type: 'add', item });
      liveIdsRef.current.add(id);
      if (input.onDismiss) dismissCbsRef.current.set(id, input.onDismiss);
      const duration = input.durationMs ?? TOAST_DURATION[tone];
      if (duration > 0) scheduleTimer(id, duration);
      return id;
    },
    [baseId, scheduleTimer],
  );

  const update = useCallback(
    (id: string, patch: ToastPatch) => {
      if (!liveIdsRef.current.has(id)) return; // já saiu do DOM — no-op
      dispatch({ type: 'update', id, patch });
      if (patch.onDismiss) dismissCbsRef.current.set(id, patch.onDismiss);

      // Quanto sobrava do timer antigo: um update parcial (só o título, digamos)
      // não pode zerar a contagem nem tornar o toast sticky sem querer.
      const prev = timersRef.current.get(id);
      const prevRemaining = prev
        ? Math.max(0, prev.remaining - (Date.now() - prev.startedAt))
        : undefined;

      // Cancela o agendado: o timer antigo e, se o update reabriu um toast que já
      // estava saindo, também a remoção pendente.
      clearDismissTimer(id);
      const leaveTimer = leaveTimersRef.current.get(id);
      if (leaveTimer) {
        clearTimeout(leaveTimer);
        leaveTimersRef.current.delete(id);
      }

      // Sem tom nem duração novos, preserva o que restava. Com tom novo (o caso
      // loading → success), passa a valer o default do tom.
      const duration =
        patch.durationMs ?? (patch.tone ? TOAST_DURATION[patch.tone] : prevRemaining);
      if (duration !== undefined && duration > 0) scheduleTimer(id, duration);
    },
    [clearDismissTimer, scheduleTimer],
  );

  const api = useMemo<ToastApi>(
    () => ({
      info: (i) => enqueue('info', i),
      success: (i) => enqueue('success', i),
      warning: (i) => enqueue('warning', i),
      danger: (i) => enqueue('danger', i),
      error: (i) => enqueue('danger', i),
      loading: (i) => enqueue('loading', i),
      update,
      dismiss: (id) => beginLeave(id),
      clear: () => {
        timersRef.current.forEach((e) => clearTimeout(e.timeoutId));
        timersRef.current.clear();
        leaveTimersRef.current.forEach(clearTimeout);
        leaveTimersRef.current.clear();
        // clear() é wipe programático, não dismiss do usuário: não dispara onDismiss.
        dismissCbsRef.current.clear();
        liveIdsRef.current.clear();
        dispatch({ type: 'clear' });
      },
    }),
    [enqueue, beginLeave, update],
  );

  // Identidade estável: o Toast usa isto como dependência do efeito que instala
  // o ResizeObserver. Recriar a função a cada render religaria o observer sempre.
  const handleHeight = useCallback((id: string, height: number) => {
    setHeights((prev) => {
      // Alturas fracionárias (zoom, fontes) fariam este set disparar a cada
      // subpixel. Arredondar corta o ciclo medir → repintar → medir.
      const rounded = Math.round(height);
      if (prev[id] === rounded) return prev;
      return { ...prev, [id]: rounded };
    });
  }, []);

  const geometry = useMemo(
    () => computeStackGeometry(state.items.map((t) => t.id), heights),
    [state.items, heights],
  );

  // Registra este provider como o alvo do atalho showError enquanto montado.
  useEffect(() => {
    activeToastApi = api;
    return () => {
      if (activeToastApi === api) activeToastApi = null;
    };
  }, [api]);

  // Reconcilia os refs com o que está de fato no DOM. O cap FIFO derruba o toast
  // mais antigo DENTRO do reducer, sem passar por remove() — sem isto o timer e o
  // onDismiss dele ficariam órfãos (o callback nunca dispararia e o timeout
  // acordaria depois pra um id que não existe mais).
  useEffect(() => {
    const alive = new Set(state.items.map((t) => t.id));

    // Alturas de toasts que já saíram vazariam pra sempre neste mapa.
    setHeights((prev) => {
      const stale = Object.keys(prev).filter((id) => !alive.has(id));
      if (stale.length === 0) return prev;
      const next = { ...prev };
      stale.forEach((id) => delete next[id]);
      return next;
    });

    liveIdsRef.current.forEach((id) => {
      if (alive.has(id)) return;
      liveIdsRef.current.delete(id);
      clearDismissTimer(id);
      const leaveTimer = leaveTimersRef.current.get(id);
      if (leaveTimer) {
        clearTimeout(leaveTimer);
        leaveTimersRef.current.delete(id);
      }
      // Derrubado pelo cap ainda é dismiss: o chamador precisa saber.
      const cb = dismissCbsRef.current.get(id);
      if (cb) {
        dismissCbsRef.current.delete(id);
        cb();
      }
    });
  }, [state.items, clearDismissTimer]);

  // Limpa timers ao desmontar — evita memory leak em testes.
  useEffect(() => {
    const timers = timersRef.current;
    const leaveTimers = leaveTimersRef.current;
    const cbs = dismissCbsRef.current;
    const liveIds = liveIdsRef.current;
    return () => {
      timers.forEach((e) => clearTimeout(e.timeoutId));
      timers.clear();
      leaveTimers.forEach(clearTimeout);
      leaveTimers.clear();
      cbs.clear();
      liveIds.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Sem aria-live AQUI de propósito: cada Toast declara o próprio
          role/aria-live conforme o tom. Uma região live envolvendo outra deixa a
          politeness ambígua (o polite externo pode engolir o assertive do erro) e
          rende anúncio duplicado. Validar com NVDA ao mexer nisto. */}
      <section
        className={styles.region}
        aria-label="Notificações"
        data-empty={state.items.length === 0 ? '' : undefined}
        style={
          {
            // As medidas da pilha nascem no TS porque `computeStackGeometry` também
            // depende delas — declará-las no CSS criaria duas fontes que driftam.
            '--toast-motion': `${TOAST_MOTION_MS}ms`,
            '--toast-peek': `${TOAST_STACK_PEEK}px`,
            '--toast-scale-step': TOAST_STACK_SCALE_STEP,
            '--toast-stack-height': `${
              expanded ? geometry.expandedHeight : geometry.collapsedHeight
            }px`,
            '--toast-front-height': `${geometry.frontHeight}px`,
          } as CSSProperties
        }
        // Abrir a pilha e pausar os timers é o mesmo gesto. onFocus/onBlur
        // borbulham (React usa focusin/focusout), então o teclado abre também —
        // sem isso o "Desfazer" de um toast de trás seria inalcançável.
        onMouseEnter={() => {
          setExpanded(true);
          pauseAll();
        }}
        onMouseLeave={() => {
          setExpanded(false);
          resumeAll();
        }}
        onFocus={() => {
          setExpanded(true);
          pauseAll();
        }}
        onBlur={() => {
          setExpanded(false);
          resumeAll();
        }}
      >
        {state.items.map((t, i) => (
          <div
            key={t.id}
            className={styles.slot}
            data-expanded={expanded ? '' : undefined}
            style={
              {
                '--toast-index': geometry.slots[i]?.index ?? 0,
                '--toast-offset': `${geometry.slots[i]?.offset ?? 0}px`,
              } as CSSProperties
            }
          >
            <Toast
              id={t.id}
              tone={t.tone}
              title={t.title}
              // 🔑 Confirmação é título só; erro mantém a descrição.
              // Em info/success o título já diz tudo ("Contato criado") e a
              // segunda linha só engorda o card. Em danger/warning a descrição
              // NÃO é enfeite: é o motivo — o que a Meta devolveu, qual campo
              // falhou. Some ela e sobra "Não foi possível enviar", que não
              // ajuda ninguém a agir.
              //
              // A regra mora AQUI, e não no Toast: 10 telas usam o componente
              // solto como banner inline (o "Em breve" do login é `info` COM
              // descrição), e aplicá-la lá dentro apagaria o texto delas.
              description={DESCRIPTION_TONES.has(t.tone) ? t.description : undefined}
              action={t.action}
              state={t.state}
              height={heights[t.id]}
              expanded={expanded}
              front={(geometry.slots[i]?.index ?? 0) === 0}
              countdownMs={t.durationMs ?? TOAST_DURATION[t.tone]}
              countdownKey={t.generation}
              onHeightChange={handleHeight}
              onDismiss={() => beginLeave(t.id)}
              onMouseEnter={() => pause(t.id)}
              onMouseLeave={() => resume(t.id)}
              onFocus={() => pause(t.id)}
              onBlur={() => resume(t.id)}
            />
          </div>
        ))}
      </section>
    </ToastContext.Provider>
  );
}
